import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { PanelsTopLeft, Plus, FileText, LayoutGrid, Boxes } from 'lucide-react'
import { PageShell } from '../../../components/layout/PageShell'
import { SecondaryRail, RailItem, RailSection } from '../../../components/layout/SecondaryRail'
import { PageBrow } from '../../../components/layout/PageBrow'
import { DataTable, TablePanel, KpiCard, KpiRow, sortRows, type Column, type SortState } from '../../../components/patterns'
import { useAuthStore } from '../../../stores/authStore'
import { demoProjects, demoKpis, type DemoProject, type DemoScope } from './mockData'

const SCOPES: DemoScope[] = ['Everyone', 'Department', 'Personal']

const columns: Column<DemoProject>[] = [
  {
    key: 'name', header: 'Name', sortAccessor: (p) => p.name, render: (p) => (
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"><FileText size={15} /></span>
        <span className="truncate text-sm font-medium text-foreground">{p.name}</span>
      </div>
    ),
  },
  { key: 'owner', header: 'Owner', width: '180px', sortAccessor: (p) => p.owner, render: (p) => <span className="text-sm text-muted-foreground">{p.owner}</span> },
  { key: 'status', header: 'Status', width: '120px', render: (p) => <span className="text-sm text-muted-foreground">{p.status}</span> },
  { key: 'members', header: 'Members', width: '110px', align: 'right', sortAccessor: (p) => p.members, render: (p) => <span className="tabular-nums text-sm text-foreground">{p.members}</span> },
  { key: 'updated', header: 'Updated', width: '130px', render: (p) => <span className="whitespace-nowrap text-sm text-muted-foreground">{p.updated}</span> },
]

export default function TwoRailDemo() {
  const user = useAuthStore((s) => s.user)
  const navigate = useNavigate()
  const [activeScope, setActiveScope] = useState<DemoScope | 'all'>('all')
  const [sort, setSort] = useState<SortState | null>(null)

  if (!user?.isAdmin) return <Navigate to="/" replace />

  const groupScopes = SCOPES.filter((s) => activeScope === 'all' || s === activeScope)
  const sections = groupScopes.map((scope) => {
    const rows = sortRows(demoProjects.filter((p) => p.scope === scope), columns, sort)
    return { key: scope, label: scope, count: rows.length, rows }
  })
  const shownCount = sections.reduce((n, s) => n + s.rows.length, 0)

  const rail2 = (
    <SecondaryRail icon={<PanelsTopLeft size={15} />} title="Projects" onCreate={() => {}} createLabel="New project" search={{ value: '', onChange: () => {}, placeholder: 'Search projects...' }}>
      <RailItem active={activeScope === 'all'} icon={<LayoutGrid size={14} />} label="All projects" count={demoProjects.length} onClick={() => setActiveScope('all')} />
      {SCOPES.map((scope) => (
        <RailSection key={scope} label={scope} count={demoProjects.filter((p) => p.scope === scope).length}>
          <RailItem active={activeScope === scope} label={`${scope} projects`} onClick={() => setActiveScope(scope)} />
        </RailSection>
      ))}
    </SecondaryRail>
  )

  const brow = (
    <PageBrow
      onBack={() => navigate('/admin')}
      left={<div className="flex items-center gap-2.5"><PanelsTopLeft size={18} className="text-primary" /><h1 className="text-[15px] font-semibold text-foreground">Projects</h1><span className="h-1 w-1 rounded-full bg-muted-foreground/50" /><span className="text-[13px] text-muted-foreground">2-rail layout demo - mock data</span></div>}
      right={<button type="button" className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"><Plus size={15} /> New project</button>}
    />
  )

  // Content fills the height with a thin grey gutter; the table lives in a
  // full-height white TablePanel (no SectionCard wrap), so there is no grey
  // expanse below a short table. Columns sort within each group.
  return (
    <PageShell rail2={rail2} brow={brow} scroll={false}>
      <div className="flex min-h-0 flex-1 flex-col gap-4 p-4">
        <KpiRow>
          {demoKpis.map((k) => <KpiCard key={k.label} label={k.label} value={k.value} accent={k.accent} icon={<Boxes size={18} />} />)}
        </KpiRow>
        <TablePanel
          icon={<LayoutGrid size={14} />}
          title={activeScope === 'all' ? 'All projects' : `${activeScope} projects`}
          count={`${shownCount} shown`}
          className="min-h-0 flex-1"
        >
          <DataTable columns={columns} sections={sections} rowKey={(p) => p.id} onRowClick={() => {}} sort={sort} onSortChange={setSort} />
        </TablePanel>
      </div>
    </PageShell>
  )
}
